import { FlexRow } from "@quarks";
import { HaNRoomListHorizontalProps } from "./types";
import { HaNCalendarSync } from "src/components/organisms/hereAndNow/HaNCalendarSync/HaNCalendarSync";
import { HaNRoomCell } from "src/components/organisms/hereAndNow/HaNRoomCell/HaNRoomCell";
import { StyledSwiperHorizontal, StyledRoomCellContainer, StyledContainer } from "./styles";
import Swiper from "swiper";
import { forwardRef, useCallback, useMemo } from "react";
import { EmptyListPlaceholder, SwiperRefType } from "@molecules";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@hooks";
import { getMeetingRoomRequestState } from "@lib/store";
import { Loader } from "@atoms";
import { useActiveMeetingRoomRequest } from "src/components/pages/HereAndNow/Context/hooks/useActiveMeetingRoomRequest";
import { useHereAndNow } from "src/components/pages/HereAndNow/Context/HereAndNowContext";
import { FloorSuggestionsTile } from "../HaNVerticalListContent/FloorSuggestions/FloorSuggestionsTile";
import { trackEvent } from "@lib/infrastructure";

/**
 * Lists (horizontally) available rooms in Here And Now
 */

export const HaNRoomListHorizontal = forwardRef<SwiperRefType, HaNRoomListHorizontalProps>(
  (
    {
      buildingId,
      onClick,
      startDateTime,
      endDateTime,
      onSlideChange,
      showCalendarConsentMessageNoOccupancy,
    }: HaNRoomListHorizontalProps,
    ref,
  ) => {
    const { t } = useTranslation();

    const { currentRoomDateTimes, tab, filterData, selectedObject, selectedFloorId } = useHereAndNow();
    const request = useActiveMeetingRoomRequest();

    const meetingRoomLoadingStatus = useAppSelector(
      state => getMeetingRoomRequestState(state, request)?.selectedFloorLoadingStatus,
    );
    const loadingMeetingRooms = meetingRoomLoadingStatus === "loading" || meetingRoomLoadingStatus === undefined;

    const { above, below } = filterData.rooms.adjacentFilteredData;

    const aboveFloorId = above.at(0)?.floorId || null;
    const belowFloorId = below.at(0)?.floorId || null;

    const listData = useMemo(() => filterData.rooms.filteredData, [filterData.rooms.filteredData]);

    const scrollToSelected = useCallback(
      (swiper: Swiper) => {
        const index = listData.findIndex(room => room.id === selectedObject?.id);
        if (index !== -1) {
          swiper.slideTo(index, 0);
        }
      },
      [listData, selectedObject?.id],
    );

    const handleSlideChange = (swiper: Swiper) => {
      const visibleRoom = listData[swiper.activeIndex];
      if (!visibleRoom) return;
      onSlideChange(visibleRoom);
    };

    return (
      <FlexRow
        columnGap={8}
        overflow="hidden"
        zIndex={2}
        justifyContent="center">
        {loadingMeetingRooms ? (
          <StyledContainer justifyContent="center">
            <Loader />
          </StyledContainer>
        ) : showCalendarConsentMessageNoOccupancy ? (
          <StyledContainer>
            <HaNCalendarSync displayIllustration={false} />
          </StyledContainer>
        ) : listData.length || aboveFloorId || belowFloorId ? (
          <StyledSwiperHorizontal
            freeMode={false}
            onSlideChange={handleSlideChange}
            onUpdate={scrollToSelected}
            ref={ref}
            centeredSlides={true}
            spaceBetween={16}>
            {listData.length > 0 ? (
              listData.map((room, index) => (
                <StyledRoomCellContainer key={room.id}>
                  <HaNRoomCell
                    onClick={onClick}
                    index={index}
                    key={room.id}
                    room={room}
                  />
                </StyledRoomCellContainer>
              ))
            ) : (
              <EmptyListPlaceholder
                text={t("hereAndNow:NoRoomsAvailable")}
                className="p-2 justify-center [&>svg]:size-14"
              />
            )}

            {aboveFloorId && (
              <StyledRoomCellContainer>
                <FloorSuggestionsTile
                  onClick={() => {
                    if (ref && "current" in ref && ref.current) {
                      ref.current.scrollToSlide(0);
                    }
                  }}
                  floorId={aboveFloorId}
                  direction={"up"}
                />
              </StyledRoomCellContainer>
            )}

            {above.map((room, index) => (
              <StyledRoomCellContainer key={room.id}>
                <HaNRoomCell
                  index={index}
                  onClick={e => {
                    onClick(e);
                    trackEvent("HereAndNow_OtherFloorSuggestions__SuggestionClick", {
                      buildingId,
                      tab,
                      floorId: aboveFloorId || "",
                      type: "above",
                      nodeId: room.id,
                    });
                  }}
                  key={room.id}
                  room={room}
                />
              </StyledRoomCellContainer>
            ))}

            {belowFloorId && (
              <StyledRoomCellContainer>
                <FloorSuggestionsTile
                  onClick={() => {
                    if (ref && "current" in ref && ref.current) {
                      ref.current.scrollToSlide(0);
                    }
                  }}
                  floorId={belowFloorId}
                  direction={"down"}
                />
              </StyledRoomCellContainer>
            )}

            {below.map((room, index) => (
              <StyledRoomCellContainer key={room.id}>
                <HaNRoomCell
                  index={index}
                  onClick={e => {
                    onClick(e);
                    trackEvent("HereAndNow_OtherFloorSuggestions__SuggestionClick", {
                      buildingId,
                      tab,
                      floorId: belowFloorId || "",
                      type: "below",
                      nodeId: room.id,
                    });
                  }}
                  key={room.id}
                  room={room}
                />
              </StyledRoomCellContainer>
            ))}
          </StyledSwiperHorizontal>
        ) : (
          <EmptyListPlaceholder text={t("hereAndNow:NoRoomsAvailable")} />
        )}
      </FlexRow>
    );
  },
);
